<template>
  <img
    v-if="assetSrc"
    class="ntl-img"
    :src="assetSrc"
    :alt="altText"
    :style="sizeStyle"
    loading="lazy"
    @error="assetFailed = true"
  />
  <span
    v-else
    class="ntl-monogram"
    :class="`ntl-monogram--${sizeClass}`"
    :style="[sizeStyle, colorStyle]"
    role="img"
    :aria-label="altText"
  >{{ monogram }}</span>
</template>

<script>
/**
 * NFL team identity tile.
 *
 * LOGO_ASSET_STATUS: the repository has no licensed NFL logo asset pack, so
 * the abbreviation monogram below is the production rendering. When approved
 * local assets land, add them to LOGO_ASSETS keyed by abbreviation (e.g.
 * BAL: require('@/images/nfl/bal.png')) and this component will use them,
 * falling back to the monogram for unknown/historical abbreviations or load
 * failures. Never renders a broken <img>.
 */
import { teamColors } from '@/utils/nflTeamColors';

const LOGO_ASSETS = {};

export default {
  name: 'NflTeamLogo',
  props: {
    /** Preferred: a team ref { abbreviation, fullName, name } */
    team: { type: Object, default: null },
    /** Or a bare abbreviation string ("BAL"). */
    abbreviation: { type: String, default: null },
    size: { type: Number, default: 36 },
  },
  data() {
    return { assetFailed: false };
  },
  computed: {
    abbr() {
      const raw = (this.team && this.team.abbreviation) || this.abbreviation || '';
      return String(raw).toUpperCase().slice(0, 4);
    },
    monogram() {
      return this.abbr || '?';
    },
    assetSrc() {
      if (this.assetFailed) return null;
      return LOGO_ASSETS[this.abbr] || null;
    },
    altText() {
      const name = (this.team && (this.team.fullName || this.team.name)) || this.abbr;
      return name ? `${name} logo` : 'Team logo';
    },
    sizeStyle() {
      const s = `${this.size}px`;
      return { width: s, height: s, fontSize: `${Math.max(9, Math.round(this.size * 0.3))}px` };
    },
    sizeClass() {
      return this.size >= 48 ? 'lg' : this.size >= 32 ? 'md' : 'sm';
    },
    /**
     * Team identity: primary-color tile, contrast-safe text, secondary-color
     * edge. Unknown/historical abbreviations return null and keep the neutral
     * slate fallback from the stylesheet.
     */
    colorStyle() {
      const c = teamColors(this.abbr);
      if (!c) return null;
      return {
        background: `linear-gradient(135deg, ${c.bg} 0%, ${c.bg} 55%, rgba(0, 0, 0, 0.22) 160%)`,
        backgroundColor: c.bg,
        color: c.text,
        boxShadow: `inset 0 0 0 1px rgba(255, 255, 255, 0.14), inset 0 -2px 0 0 ${c.accent}`,
      };
    },
  },
};
</script>

<style scoped>
.ntl-img {
  display: inline-block;
  object-fit: contain;
  flex-shrink: 0;
}
.ntl-monogram {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 10px;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  color: #e2e8f0;
  font-weight: 900;
  letter-spacing: 0.5px;
  font-family: 'Titillium Web', sans-serif;
  user-select: none;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
}
.ntl-monogram--sm { border-radius: 7px; }
.ntl-monogram--lg { border-radius: 12px; }
</style>
